import { ProFormSelect, ProFormGroup, ProFormField, ProForm } from '@ant-design/pro-form';
import { useLocalStorageState } from 'ahooks';
import { Select, AutoComplete, Tooltip } from 'antd';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import classnames from 'classnames';
import React, { FC, useState  } from 'react';
import { useTranslation } from 'react-i18next';
import { storageGet } from '@/infrastructure/storage-utils';
import { DetailSelectGroupFormType } from '@/presentation/components/form';
import { useOptions } from '@/presentation/components/form/utils';
import { DetailOperator, ExOption, FieldKey, getLocalField, LaunchType, Option, DetailModeOperator } from '@/proto/field';
import { MetricSubType, MetricType } from '@/proto/metric';
import { toHumpString } from '@raft/utils/src/case/camel-case';
import styles from './detail-select-group.module.scss';

interface DetailSelectGroupProps {
  form: FormInstance,
  leftName: string;
  centerName: string;
  rightName: string;
  leftOptions: Option[] | ExOption[];
  centerOptions: Option[] | ExOption[];
  leftInitialValue?: any;
  centerInitialValue?: any;
  rightInitialValue?: any;
  leftClassName?: string;
  centerClassName?: string;
  rightClassName?: string;
  subType?: LaunchType | MetricType | MetricSubType;
  isCompareOpen?: boolean;
  hasMaxWidth?: boolean;
  showLeftSelect?: boolean;
}


interface AutoSelectProps{
  className: string | undefined;
  options: { value: any; label: any; 'data-tid': string; }[];
  value?: string[];
  onChange?: (val: string[] | undefined) => void;
  updateRightOptions: () => void;
}

interface RightSelectProps {
  value?: string[];
  onChange?: (val: string[] | undefined) => void;
  selectChange: (value: string[]) => void;
  onSearch: (val: string) => void;
  searchValue: string;
  onClear: () => void;
  className: string | undefined;
  options: { value: any; label: any; 'data-tid': string; }[];
}

const AutoSelect = ({ className, options, value, onChange, updateRightOptions }: AutoSelectProps) => {
  const { t } = useTranslation();
  const autoChange = (val: string) => {
    if (val === '') {
      onChange?.([]);
      updateRightOptions();
    } else {
      onChange?.([val]);
    }
  };

  return <Tooltip title={value?.join?.('，')} overlayStyle={{ maxWidth: 500 }}>
    <AutoComplete
    value={Array.isArray(value) ? value?.[0] || '' : ''}
    onChange={autoChange}
    placeholder={t('请输入')}
    className={className}
    options={options}
    filterOption={(value, options) => {
      if (value && options) {
        return options.label.includes(value);
      }
      return false;
    }}
  />
  </Tooltip>;
};

const RightSelect = (props: RightSelectProps) => {
  const { value, onChange, selectChange, onSearch, searchValue, onClear, className, options } = props;
  const { t } = useTranslation();
  const change = (value: string[]) => {
    onChange?.(value);
    selectChange?.(value);
  };
  return <Tooltip title={value?.join?.('，')} overlayStyle={{ maxWidth: 500 }}>
    <Select options={options}
            value={value}
            maxTagCount={'responsive'}
            className={className}
            mode='tags'
            allowClear
            placeholder={t('请输入')}
            showSearch={true}
            onChange={change}
            autoClearSearchValue={true}
            dropdownClassName={'reset-font-size'}
            onSearch={onSearch}
            searchValue={searchValue}
            onClear={onClear}
    />
  </Tooltip>;
};

export const DETAIL_SELECT_OPTION_STORAGE_NAME = 'detailSelectOption';
export const DetailSelectGroup: FC<DetailSelectGroupProps> = (props) => {
  const { showLeftSelect = true } = props;
  const [detailSelectOption, setDetailSelectOption] = useLocalStorageState<any[]>(
    DETAIL_SELECT_OPTION_STORAGE_NAME,
    { defaultValue: [] },
  );
  const [searchValue, setSearchValue] = useState<string>('');
  const onChangeOperator = () => {
    props.form.resetFields([props.rightName, props.centerName]);
  };


  const selectChange = (value: string[]) => {
    setSearchValue('');
    if (!value?.length) {
      updateRightOptions();
    }
  };

  const centerChange = () => {
    props.form.resetFields([props.rightName]);
  };

  const rightOptions = detailSelectOption?.filter(str => !!(str && typeof (str) === 'string'));

  const onSearch = (val: string) => {
    setSearchValue(val);
  };

  // 更新下拉数据
  const updateRightOptions = () => {
    try {
      const list = JSON.parse(storageGet(DETAIL_SELECT_OPTION_STORAGE_NAME) || '[]');
      if (Array.isArray(list)) {
        setDetailSelectOption(list.filter(str => typeof(str) === 'string'));
      }
    } catch (e) {
      setDetailSelectOption([]);
      console.error('json转化失败');
    }
  };

  return <div className={styles.detailSelectGroup}>
    <ProFormGroup>
      <ProFormSelect
        hidden={!showLeftSelect}
        name={props.leftName}
        options={props.leftOptions?.map?.(item => ({
          ...item,
          ['data-tid']: `isParticiple-${item.label}`,
        }))}
        allowClear={false}
        initialValue={props.leftInitialValue}
        fieldProps={{
          dropdownClassName: 'reset-font-size',
          onChange: onChangeOperator,
        }}
      />
      <ProForm.Item
        shouldUpdate={(prevValues, curValues) => prevValues[props.leftName] !== curValues[props.leftName]}>
        {({ getFieldValue }) => {
          const operator  = getFieldValue([props.leftName]);
          let centerOptions: ExOption[] | undefined = [];
          if (operator === DetailOperator.On) {
            centerOptions = getLocalField(FieldKey.DetailMode);
          } else if (operator === DetailOperator.Off) {
            centerOptions = getLocalField(FieldKey.OffDetailMode);
          }
          return <ProFormSelect
            name={props.centerName}
            options={centerOptions?.map?.(item => ({
              ...item,
              ['data-tid']: `detailMode-${item.label}`,
            }))}
            allowClear={false}
            initialValue={props.centerInitialValue}
            fieldProps={{
              dropdownClassName: 'reset-font-size',
              onChange: centerChange,
            }}

          />;
        }}

      </ProForm.Item>

      <ProForm.Item
        shouldUpdate={(prevValues, curValues) => prevValues[props.centerName] !== curValues[props.centerName]}>
        {({ getFieldValue }) => {
          if (getFieldValue(props.centerName) === DetailModeOperator.FuzzyNatch) {
            return <ProForm.Item initialValue={props.rightInitialValue} name={props.rightName}>
              <AutoSelect
                className={props.rightClassName}
                updateRightOptions={updateRightOptions}
                options={rightOptions?.map?.(str => ({
                  value: str,
                  label: str,
                  ['data-tid']: `detail-${str}`,
                })) ?? []} />
            </ProForm.Item>;
          }

          return <ProForm.Item initialValue={props.rightInitialValue} name={props.rightName}>
            <RightSelect options={rightOptions?.map?.(str => ({
              value: str,
              label: str,
              ['data-tid']: `detail-${str}`,
            })) ?? []}
             className={props.rightClassName}
             onSearch={onSearch}
             searchValue={searchValue}
             onClear={updateRightOptions}
             selectChange={selectChange}
            />

          </ProForm.Item>;
        }}
      </ProForm.Item>
    </ProFormGroup>
  </div>;
};

export const DetailSelectGroupForm: FC<{
  entry: DetailSelectGroupFormType,
  form: FormInstance,
  hidden?: boolean,
}> = (props) => {
  const { entry, form, hidden } = props;
  const {
    label,
    leftInitialValue,
    rightInitialValue,
    centerInitialValue,
    leftName,
    centerName,
    name,
    scope,
    subType,
    isCompareOpen,
    hasMaxWidth,
    showLeftSelect,
    rightClassName,
  } = entry;
  const isAnalytics = scope === 'analytics';
  const leftFieldKey = 'leftFieldKey' in entry ? (entry as DetailSelectGroupFormType).leftFieldKey : undefined;
  const centerFieldKey = 'centerFieldKey' in entry ? (entry as DetailSelectGroupFormType).centerFieldKey : undefined;
  const fieldScope = 'fieldScope' in entry ? (entry as DetailSelectGroupFormType).fieldScope : undefined;
  const leftUserOptions = 'leftOptions' in entry ? (entry as DetailSelectGroupFormType).leftOptions : undefined;
  const centerUserOptions = 'centerOptions' in entry ? (entry as DetailSelectGroupFormType).centerOptions : undefined;

  const leftOptions = useOptions({
    fieldKey: leftFieldKey,
    fieldScope,
    userOptions: leftUserOptions,
  });
  const centerOptions = useOptions({
    fieldKey: centerFieldKey,
    fieldScope,
    userOptions: centerUserOptions,
  });


  const leftRename = isCompareOpen ? `${toHumpString(leftName)}Compare` : toHumpString(leftName);
  const centerRename = isCompareOpen ? `${toHumpString(centerName)}Compare` : toHumpString(centerName);

  return <ProFormField label={label} hidden={hidden} name={name}>
    <DetailSelectGroup
      form={form}
      leftName={leftRename}
      leftOptions={leftOptions}
      leftInitialValue={leftInitialValue}
      centerName={centerRename}
      centerOptions={centerOptions}
      centerInitialValue={centerInitialValue}
      rightName={name}
      rightInitialValue={rightInitialValue}
      rightClassName={classnames({
        [styles.rightAnalyticsForm]: isAnalytics,
        [styles.rightIssueForm]: hasMaxWidth,
      }, rightClassName)}
      subType={subType}
      isCompareOpen={isCompareOpen}
      hasMaxWidth={hasMaxWidth}
      showLeftSelect={showLeftSelect}
    />
  </ProFormField>;
};
